// G3 : registre des incidents (charte D-038, « exceptions d'abord »). Liste filtrée côté serveur avec les
// incidents majeurs en cours et les signalements à qualifier en tête ; signalement utilisable sans réseau
// (file hors connexion, identifiant client, aucun doublon) ; fiche avec qualification, mesures horodatées,
// clôture motivée, pièces et historique. Le registre ne remplace ni les dispositifs HSE ni les déclarations
// officielles et ne qualifie aucune responsabilité.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationFiche, InstallationListe } from '../../api/types';
import { useFiltres } from '../../app/filtres';
import { aUnRole, useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, libelleProvince } from '../../composants/format';
import { estErreurReseau, horsLigneDisponible, mettreEnFile } from '../inspections/horsligne';
import { CLE_OPERATIONS, useEnLigne } from '../inspections/Synchronisation';
import '../inspections/terrain.css';

interface Libelle {
  code: string | number;
  libelle: string;
}
export interface ReferentielIncidents {
  types: Libelle[];
  gravites: Libelle[];
  etats: Libelle[];
}

export function useReferentielIncidents() {
  return useQuery({ queryKey: ['incidents', 'referentiel'], queryFn: async () => (await api<ReferentielIncidents>('/api/v1/incidents/referentiel')).donnees, staleTime: 5 * 60_000 });
}
export const libelleIncident = (l: Libelle[] | undefined, c: string | number) => l?.find((x) => x.code === c)?.libelle ?? String(c);

/** Rôles qui qualifient, clôturent et annulent (contrôle) ; le serveur vérifie de nouveau chaque action. */
export const ROLES_CONTROLE_INCIDENTS = ['administrateur_national', 'directeur_general', 'directeur', 'chef_service', 'superviseur'];
const ROLES_VUE_REDUITE = ['ministre_cabinet', 'secretaire_general', 'autorite_provinciale'];

/** Gravité : toujours un libellé ; la couleur n'apparaît qu'à partir de la gravité 3 (majeure). */
export function Gravite({ g, libelle }: { g: number; libelle?: string }) {
  const classe = g >= 4 ? 'gravite critique' : g === 3 ? 'gravite majeure' : 'gravite';
  return (
    <span className={classe}>
      <i aria-hidden="true">
        {[1, 2, 3, 4].map((n) => (
          <span key={n} className={n <= g ? 'plein' : undefined} />
        ))}
      </i>
      {libelle ?? `Gravité ${g}`}
    </span>
  );
}

export interface LigneIncident {
  id: string;
  reference: string;
  type: string;
  gravite: number;
  etat: string;
  survenuLe: string;
  lieu: string;
  provinceCode: string | null;
  installationId: string | null;
  installationNom: string | null;
  organisationNom: string;
}

export function useIncidents(filtres: URLSearchParams = new URLSearchParams(), actif = true) {
  return useQuery({ queryKey: ['incidents', 'liste', filtres.toString()], queryFn: async () => (await api<LigneIncident[]>(`/api/v1/incidents?${filtres.toString()}`)).donnees, enabled: actif });
}

export function useIncidentsMajeurs(actif = true) {
  return useQuery({ queryKey: ['incidents', 'majeurs'], queryFn: async () => (await api<{ nombre: number; incidents: LigneIncident[] }>('/api/v1/incidents/majeurs')).donnees, enabled: actif });
}

const SEGMENTS = [
  { etat: '', libelle: 'Tous' },
  { etat: 'signale', libelle: 'Signalés' },
  { etat: 'qualifie', libelle: 'Qualifiés' },
  { etat: 'en_traitement', libelle: 'En traitement' },
  { etat: 'cloture', libelle: 'Clôturés' },
];

export function RegistreIncidents() {
  const [params, setParams] = useSearchParams();
  const { province: provinceGlobale } = useFiltres();
  const { etat: session } = useSession();
  const ref = useReferentielIncidents();
  const etat = params.get('etat') ?? '';
  const type = params.get('type') ?? '';
  const graviteMin = params.get('graviteMin') ?? '';
  const province = params.get('province') ?? provinceGlobale ?? '';
  const filtres = new URLSearchParams();
  for (const [k, v] of Object.entries({ etat, type, graviteMin, province })) if (v) filtres.set(k, v);
  const requete = useIncidents(filtres);
  const majeurs = useIncidentsMajeurs();
  const controle = aUnRole(session, ...ROLES_CONTROLE_INCIDENTS);
  const reduite = aUnRole(session, ...ROLES_VUE_REDUITE) && !controle;
  const aQualifier = useIncidents(new URLSearchParams({ etat: 'signale' }), controle);
  const changer = (cle: string, valeur: string) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur);
    else p.delete(cle);
    setParams(p, { replace: true });
  };

  const exceptions: { cle: string; ton: 'rouge' | 'ambre'; marque: string; titre: string; detail: string; lien: string }[] = [];
  for (const i of majeurs.data?.incidents ?? []) {
    exceptions.push({
      cle: i.id,
      ton: i.gravite >= 4 ? 'rouge' : 'ambre',
      marque: `${libelleIncident(ref.data?.gravites, i.gravite)}, ${libelleIncident(ref.data?.etats, i.etat).toLowerCase()}`,
      titre: `${libelleIncident(ref.data?.types, i.type)}, ${i.installationNom ?? i.lieu}`,
      detail: `${i.reference}, survenu le ${dateHeure(i.survenuLe)}, ${libelleProvince(i.provinceCode)}`,
      lien: `/incidents/${i.id}`,
    });
  }
  const nonQualifies = (aQualifier.data ?? []).filter((i) => i.gravite < 3);
  if (nonQualifies.length > 0)
    exceptions.push({ cle: 'a-qualifier', ton: 'ambre', marque: 'Qualification, en attente', titre: `${formaterNombre(nonQualifies.length)} signalement${nonQualifies.length > 1 ? 's' : ''} à qualifier`, detail: 'Gravité et type à confirmer par le contrôle', lien: '/incidents?etat=signale' });

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Incidents</h1>
          <p>
            {requete.data ? `${formaterNombre(requete.data.length)} incident${requete.data.length > 1 ? 's' : ''}, ` : ''}
            {province ? libelleProvince(province) : 'tout votre périmètre'}. Le registre ne remplace ni les dispositifs HSE ni les déclarations officielles.
          </p>
        </div>
        {!reduite && (
          <Link className="bouton primaire" to="/incidents/signaler">
            <Icone nom="incident" />
            Signaler un incident
          </Link>
        )}
      </div>
      {reduite && <p className="note">Vue limitée aux incidents majeurs (gravité 3 et 4), sans quantités, organisations concernées ni pièces.</p>}

      {majeurs.data && (
        <section className="a-traiter" aria-labelledby="titre-majeurs">
          <h2 id="titre-majeurs" className="sur-titre">
            {exceptions.length === 0 ? 'Aucun incident majeur en cours dans votre périmètre' : reduite ? `${majeurs.data.nombre} incident${majeurs.data.nombre > 1 ? 's' : ''} majeur${majeurs.data.nombre > 1 ? 's' : ''} en cours` : 'Ce qui demande une action'}
          </h2>
          {exceptions.length > 0 && (
            <ol className="liste-exceptions">
              {exceptions.map((e, n) => (
                <li key={e.cle}>
                  <Link className={`exception ${e.ton}`} to={e.lien}>
                    <span className="numero" aria-hidden="true">
                      {n + 1}
                    </span>
                    <span className="texte">
                      <span className="marque-exception">{e.marque}</span>
                      <b>{e.titre}</b>
                      <small>{e.detail}</small>
                    </span>
                    <Icone nom="chevron" />
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      <div className="barre-filtres" role="search" aria-label="Filtres des incidents">
        <div className="segments filtres-puces" role="radiogroup" aria-label="Statut">
          {SEGMENTS.map((s) => (
            <button key={s.etat || 'tous'} type="button" role="radio" aria-checked={etat === s.etat} onClick={() => changer('etat', s.etat)}>
              {s.libelle}
            </button>
          ))}
        </div>
        <div className="champ">
          <label htmlFor="inc-type">Type</label>
          <select id="inc-type" value={type} onChange={(e) => changer('type', e.target.value)}>
            <option value="">Tous</option>
            {ref.data?.types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="inc-gravite">Gravité minimale</label>
          <select id="inc-gravite" value={graviteMin} onChange={(e) => changer('graviteMin', e.target.value)}>
            <option value="">Toutes</option>
            {ref.data?.gravites.map((g) => (
              <option key={g.code} value={g.code}>
                {g.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="inc-province">Province</label>
          <select id="inc-province" value={province} onChange={(e) => changer('province', e.target.value)}>
            <option value="">Toutes</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {requete.isLoading && <Squelette lignes={6} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data && <TableauIncidents lignes={requete.data} referentiel={ref.data} />}
    </div>
  );
}

export function TableauIncidents({ lignes, referentiel }: { lignes: LigneIncident[]; referentiel: ReferentielIncidents | undefined }) {
  return (
    <div className="tableau-conteneur">
      <table className="tableau lignes-48">
        <caption className="visuellement-cache">Incidents</caption>
        <thead>
          <tr>
            <th scope="col">Incident</th>
            <th scope="col">Gravité</th>
            <th scope="col" className="masquable">
              Lieu
            </th>
            <th scope="col" className="masquable">
              Survenu le
            </th>
            <th scope="col">Statut</th>
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 && (
            <tr>
              <td colSpan={5} className="attenue">
                Aucun incident ne correspond à ces critères dans votre périmètre.
              </td>
            </tr>
          )}
          {lignes.map((i) => (
            <tr key={i.id}>
              <td>
                <Link className="lien-ligne" to={`/incidents/${i.id}`}>
                  {libelleIncident(referentiel?.types, i.type)}
                </Link>
                <small>{i.reference}</small>
              </td>
              <td>
                <Gravite g={i.gravite} />
              </td>
              <td className="masquable">
                {i.installationNom ?? i.lieu}
                <small>{libelleProvince(i.provinceCode)}</small>
              </td>
              <td className="masquable">{dateHeure(i.survenuLe)}</td>
              <td>
                <span className={i.etat === 'signale' ? 'etiquette ambre' : i.etat === 'annule' ? 'etiquette gris' : 'etiquette'}>{libelleIncident(referentiel?.etats, i.etat)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Signalement -----------------------------------------------------------------------------------------

/** Date et heure locales du Gabon (UTC+1) au format du champ « datetime-local ». */
function maintenantGabon(): string {
  return new Date(Date.now() + 3_600_000).toISOString().slice(0, 16);
}

export function SignalerIncident() {
  const naviguer = useNavigate();
  const client = useQueryClient();
  const { etat: session } = useSession();
  const enLigne = useEnLigne();
  const ref = useReferentielIncidents();
  const [identifiantClient, setIdentifiantClient] = useState(() => crypto.randomUUID());
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [installation, setInstallation] = useState<{ id: string; nom: string } | null>(null);
  const [type, setType] = useState('');
  const [gravite, setGravite] = useState('');
  const [survenuLe, setSurvenuLe] = useState(maintenantGabon);
  const [lieu, setLieu] = useState('');
  const [description, setDescription] = useState('');
  const [consequences, setConsequences] = useState('');
  const [mesures, setMesures] = useState('');
  const [enFile, setEnFile] = useState(false);

  // Gérant d'une seule installation : elle est proposée d'office.
  const unique = session?.affectations?.length === 1 && session.affectations[0]!.perimetre.installations.length === 1 ? session.affectations[0]!.perimetre.installations[0]! : null;
  const propre = useQuery({ queryKey: ['installation', unique], queryFn: async () => (await api<InstallationFiche>(`/api/v1/installations/${unique}`)).donnees, enabled: Boolean(unique) && enLigne, retry: false });
  useEffect(() => {
    if (propre.data && !installation) setInstallation({ id: propre.data.id, nom: propre.data.nom });
  }, [propre.data, installation]);
  const installations = useQuery({
    queryKey: ['installations', 'incident', recherche],
    enabled: recherche.length >= 2 && enLigne,
    queryFn: async () => (await api<InstallationListe[]>(`/api/v1/installations?q=${encodeURIComponent(recherche)}&limite=20`)).donnees,
  });
  const envoi = useMutation({
    mutationFn: async () => {
      const charge = {
        type,
        gravite: Number(gravite),
        // Saisie en heure du Gabon (UTC+1).
        survenuLe: `${survenuLe}:00+01:00`,
        lieu: lieu.trim() || installation?.nom || '',
        ...(installation ? { installationId: installation.id } : {}),
        description,
        consequences,
        ...(mesures.trim() ? { mesures } : {}),
      };
      if (enLigne) {
        try {
          return { mode: 'serveur' as const, id: (await api<{ id: string }>('/api/v1/incidents', { corps: { identifiantClient, ...charge } })).donnees.id };
        } catch (e) {
          if (!estErreurReseau(e) || !horsLigneDisponible()) throw e;
        }
      }
      if (!horsLigneDisponible()) throw new Error('Signalement impossible sans réseau sur ce navigateur');
      await mettreEnFile('signalement_incident', charge, null, new Date(charge.survenuLe));
      return { mode: 'file' as const, id: null };
    },
    onSuccess: async (r) => {
      await client.invalidateQueries({ queryKey: ['incidents'] });
      await client.invalidateQueries({ queryKey: CLE_OPERATIONS });
      if (r.mode === 'serveur' && r.id) naviguer(`/incidents/${r.id}`);
      else {
        setEnFile(true);
        setIdentifiantClient(crypto.randomUUID());
        setDescription('');
        setConsequences('');
        setMesures('');
      }
    },
  });
  const pret = Boolean(type && gravite && description.trim().length >= 3 && consequences.trim().length >= 3 && (lieu.trim().length >= 3 || installation));

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Signaler un incident</h1>
          <p>Décrivez les faits et leurs conséquences. La gravité indiquée est une estimation, qualifiée ensuite par le contrôle de la DGH.</p>
        </div>
      </div>
      {!enLigne && <p className="note ambre">Hors connexion : le signalement sera enregistré chiffré sur cet appareil et envoyé au retour du réseau, sans doublon.</p>}
      {enFile && (
        <Message type="succes">
          Signalement enregistré sur cet appareil, en attente d’envoi. Son état apparaît dans le bloc de synchronisation de votre accueil.
        </Message>
      )}
      <form
        className="carte-bloc"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (pret) envoi.mutate();
        }}
      >
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="di-type">Type d’incident</label>
            <select id="di-type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Choisir</option>
              {ref.data?.types.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.libelle}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="di-date">Survenu le (heure du Gabon)</label>
            <input id="di-date" type="datetime-local" value={survenuLe} max={maintenantGabon()} onChange={(e) => setSurvenuLe(e.target.value)} />
          </div>
        </div>
        <fieldset className="champ">
          <legend>Gravité estimée</legend>
          <div className="segments" role="radiogroup" aria-label="Gravité estimée">
            {(ref.data?.gravites ?? []).map((g) => (
              <button key={g.code} type="button" role="radio" aria-checked={gravite === String(g.code)} onClick={() => setGravite(String(g.code))}>
                {g.libelle}
              </button>
            ))}
          </div>
          <span className="aide">Gravité 3 et 4 : l’incident remonte au tableau de bord ministériel dès sa réception.</span>
        </fieldset>
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="di-q">Installation concernée (facultatif)</label>
            {installation ? (
              <div className="actions">
                <span className="etiquette">{installation.nom}</span>
                <button type="button" className="bouton discret" onClick={() => setInstallation(null)}>
                  Changer
                </button>
              </div>
            ) : (
              <input
                id="di-q"
                type="search"
                disabled={!enLigne}
                placeholder={enLigne ? 'Nom de l’installation' : 'Recherche indisponible sans réseau'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onBlur={() => setRecherche(q.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setRecherche(q.trim());
                  }
                }}
              />
            )}
          </div>
          <div className="champ">
            <label htmlFor="di-lieu">Lieu précis</label>
            <input id="di-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder={installation ? 'Par exemple : piste de distribution' : 'Route, site, quai'} />
          </div>
        </div>
        {!installation && installations.data && installations.data.length > 0 && (
          <ul className="choix-installation" aria-label="Installations trouvées">
            {installations.data.map((i) => (
              <li key={i.id}>
                <label>
                  <input type="radio" name="di-installation" onChange={() => setInstallation({ id: i.id, nom: i.nom })} />
                  <span>
                    {i.nom}
                    <small>{libelleProvince(i.provinceCode)}</small>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="champ">
          <label htmlFor="di-description">Description des faits</label>
          <textarea id="di-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="di-consequences">Conséquences</label>
          <textarea id="di-consequences" rows={2} value={consequences} onChange={(e) => setConsequences(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="di-mesures">Premières mesures prises (facultatif)</label>
          <textarea id="di-mesures" rows={2} value={mesures} onChange={(e) => setMesures(e.target.value)} />
        </div>
        {envoi.error && <MessageErreur erreur={envoi.error} titre="Signalement refusé" />}
        <div className="actions">
          <Link className="bouton discret" to="/incidents">
            Annuler
          </Link>
          <button type="submit" className="bouton primaire" disabled={!pret || envoi.isPending}>
            {enLigne ? 'Signaler l’incident' : 'Enregistrer sur cet appareil'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Fiche ----------------------------------------------------------------------------------------------

interface FicheIncidentDonnees {
  id: string;
  reference: string;
  type: string;
  gravite: number;
  graviteInitiale: number;
  etat: string;
  survenuLe: string;
  lieu: string;
  provinceCode: string | null;
  installationId: string | null;
  installationNom: string | null;
  organisationNom: string | null;
  organisationsConcerneesNoms?: string[];
  produitCode: string | null;
  quantiteConcernee?: string | null;
  unite?: string | null;
  description: string;
  consequences: string;
  mesures: string | null;
  resolution: string | null;
  alerteId: string | null;
  ordreTransportId: string | null;
  source: string;
  vueReduite?: boolean;
  historique: { id: string; type: string; commentaire: string; auteur: string | null; creeLe: string }[];
  pieces: { id: string; titre: string; taille: number; creeLe: string }[];
  droits: { qualifier: boolean; mesurer: boolean; cloturer: boolean; annuler: boolean; joindre: boolean };
}

export function FicheIncident() {
  const { id } = useParams();
  const client = useQueryClient();
  const ref = useReferentielIncidents();
  const requete = useQuery({ queryKey: ['incident', id], queryFn: async () => (await api<FicheIncidentDonnees>(`/api/v1/incidents/${id}`)).donnees });
  const [message, setMessage] = useState<string | null>(null);
  const [erreurPiece, setErreurPiece] = useState<unknown>(null);
  const action = useMutation({
    mutationFn: async (p: { chemin: string; corps: unknown; succes: string }) => {
      await api(`/api/v1/incidents/${id}/${p.chemin}`, { corps: p.corps });
      return p.succes;
    },
    onSuccess: async (texte) => {
      setMessage(texte);
      await client.invalidateQueries({ queryKey: ['incident', id] });
      await client.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
  if (requete.isLoading) return <Squelette lignes={8} />;
  if (requete.error || !requete.data) return <MessageErreur erreur={requete.error} titre="Incident introuvable ou hors de votre périmètre" />;
  const f = requete.data;
  const titreType = libelleIncident(ref.data?.types, f.type);
  const enCours = ['signale', 'qualifie', 'en_traitement'].includes(f.etat);

  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="contexte">
            {f.reference}, déclaré par {f.organisationNom ?? 'organisation non lisible'}
            {f.source === 'hors_ligne' ? ', saisi hors connexion' : ''}
          </span>
          <h1>
            {titreType}, {f.installationNom ?? f.lieu}
          </h1>
          <div className="actions">
            <Gravite g={f.gravite} libelle={libelleIncident(ref.data?.gravites, f.gravite)} />
            <span className={f.etat === 'signale' ? 'etiquette ambre' : f.etat === 'annule' ? 'etiquette gris' : 'etiquette'}>{libelleIncident(ref.data?.etats, f.etat)}</span>
            <span className="attenue">survenu le {dateHeure(f.survenuLe)}</span>
          </div>
        </div>
        {f.installationId && (
          <Link className="bouton" to={`/referentiel/installations/${f.installationId}`}>
            Fiche de l’installation
          </Link>
        )}
      </div>
      {f.vueReduite && <p className="note">Vue réduite des incidents majeurs : quantités, organisations concernées, pièces et historique ne sont pas affichés à votre niveau.</p>}
      {message && <Message type="succes">{message}</Message>}
      {action.error && <MessageErreur erreur={action.error} titre="Action refusée" />}

      {f.droits.qualifier && f.etat === 'signale' && <Qualification fiche={f} referentiel={ref.data} envoyer={action.mutate} enCours={action.isPending} />}
      {enCours && f.etat !== 'signale' && (f.droits.mesurer || f.droits.cloturer) && (
        <section className={f.gravite >= 4 ? 'a-faire rouge' : 'a-faire'} aria-labelledby="titre-traitement">
          <div>
            <span className="sur-titre">À faire maintenant</span>
            <h2 id="titre-traitement">{f.gravite >= 3 ? 'Incident majeur en traitement' : 'Incident en traitement'}</h2>
            <p>Consignez chaque mesure prise (elle est horodatée) ; la clôture exige une résolution motivée.{f.gravite >= 3 && !f.droits.cloturer ? ' La clôture d’un incident majeur revient au contrôle (DGH).' : ''}</p>
          </div>
          <div className="actions decision">
            {f.droits.mesurer && <ActionTexte libelle="Consigner une mesure" etiquette="Mesure prise" champ="mesure" chemin="mesures" envoyer={action.mutate} succes="Mesure consignée." />}
            {f.droits.cloturer && <ActionTexte libelle="Clôturer" etiquette="Résolution" champ="resolution" chemin="cloture" envoyer={action.mutate} succes="Incident clôturé." principale />}
          </div>
        </section>
      )}

      <div className="deux-colonnes">
        <section className="principal" aria-label="Faits et traitement">
          <div className="carte-bloc">
            <h2>Faits</h2>
            <p>{f.description}</p>
            <h3>Conséquences</h3>
            <p>{f.consequences}</p>
            <h3>Mesures</h3>
            {f.mesures ? <p className="texte-lignes">{f.mesures}</p> : <p className="attenue">Aucune mesure consignée.</p>}
            {f.resolution && (
              <>
                <h3>Résolution</h3>
                <p>{f.resolution}</p>
              </>
            )}
          </div>
          {!f.vueReduite && (
            <div className="carte-bloc">
              <h2>Historique</h2>
              {f.historique.length === 0 ? (
                <p className="attenue">Aucun événement.</p>
              ) : (
                <ol className="historique">
                  {f.historique.map((h) => (
                    <li key={h.id}>
                      <time dateTime={h.creeLe}>{dateHeure(h.creeLe)}</time>
                      <span>
                        {h.auteur ?? 'Auteur non lisible'} : {h.commentaire}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
          {f.droits.annuler && (
            <details className="zone-sensible">
              <summary>Annuler ce signalement (déclaration erronée)</summary>
              <ActionTexte libelle="Annuler le signalement" etiquette="Motif de l’annulation" champ="motif" chemin="annulation" envoyer={action.mutate} succes="Signalement annulé ; il reste consultable." danger ouvertDOffice />
            </details>
          )}
        </section>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="titre-fiche-incident">
            <h2 id="titre-fiche-incident">Fiche</h2>
            <dl className="paires">
              <div>
                <dt>Lieu</dt>
                <dd>
                  {f.lieu}
                  <small>{libelleProvince(f.provinceCode)}</small>
                </dd>
              </div>
              <div>
                <dt>Gravité initiale</dt>
                <dd>{libelleIncident(ref.data?.gravites, f.graviteInitiale)}</dd>
              </div>
              {f.organisationsConcerneesNoms && f.organisationsConcerneesNoms.length > 0 && (
                <div>
                  <dt>Organisations concernées</dt>
                  <dd>{f.organisationsConcerneesNoms.join(', ')}</dd>
                </div>
              )}
              {f.quantiteConcernee && (
                <div>
                  <dt>Quantité concernée</dt>
                  <dd>
                    {f.quantiteConcernee} {f.unite}
                    {f.produitCode && <small>{f.produitCode}</small>}
                  </dd>
                </div>
              )}
              {f.alerteId && (
                <div>
                  <dt>Alerte liée</dt>
                  <dd>{f.alerteId.slice(0, 8)}</dd>
                </div>
              )}
              {f.ordreTransportId && (
                <div>
                  <dt>Livraison liée</dt>
                  <dd>{f.ordreTransportId.slice(0, 8)}</dd>
                </div>
              )}
            </dl>
          </section>
          {!f.vueReduite && (
            <section className="carte-bloc" aria-labelledby="titre-pieces-incident">
              <h2 id="titre-pieces-incident">Pièces</h2>
              {f.pieces.length === 0 ? (
                <p className="attenue">Aucune pièce jointe.</p>
              ) : (
                <ul className="liste-doublons">
                  {f.pieces.map((p) => (
                    <li key={p.id}>
                      <span>
                        <b>{p.titre}</b>
                        <small>{Math.ceil(p.taille / 1024)} Ko</small>
                      </span>
                      <button
                        type="button"
                        className="bouton"
                        aria-label={`Télécharger ${p.titre}`}
                        onClick={() =>
                          void api<{ url: string }>(`/api/v1/incidents/pieces/${p.id}/lien`, { corps: {} })
                            .then((l) => window.location.assign(l.donnees.url))
                            .catch(setErreurPiece)
                        }
                      >
                        Télécharger
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {erreurPiece !== null && <MessageErreur erreur={erreurPiece} titre="Téléchargement refusé" />}
              {f.droits.joindre && <JoindrePiece incidentId={f.id} surAjout={() => client.invalidateQueries({ queryKey: ['incident', id] })} />}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function ActionTexte(p: {
  libelle: string;
  etiquette: string;
  champ: string;
  chemin: string;
  envoyer: (x: { chemin: string; corps: unknown; succes: string }) => void;
  succes: string;
  principale?: boolean;
  danger?: boolean;
  ouvertDOffice?: boolean;
}) {
  const [ouvert, setOuvert] = useState(Boolean(p.ouvertDOffice));
  const [texte, setTexte] = useState('');
  const classe = p.danger ? 'bouton danger' : p.principale ? 'bouton primaire' : 'bouton';
  if (!ouvert)
    return (
      <button type="button" className={classe} onClick={() => setOuvert(true)}>
        {p.libelle}
      </button>
    );
  return (
    <form
      className="action-motivee"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        p.envoyer({ chemin: p.chemin, corps: { [p.champ]: texte }, succes: p.succes });
        setTexte('');
        if (!p.ouvertDOffice) setOuvert(false);
      }}
    >
      <div className="champ">
        <label htmlFor={`it-${p.chemin}`}>{p.etiquette}</label>
        <textarea id={`it-${p.chemin}`} rows={2} value={texte} onChange={(e) => setTexte(e.target.value)} />
      </div>
      <div className="actions">
        {!p.ouvertDOffice && (
          <button type="button" className="bouton discret" onClick={() => setOuvert(false)}>
            Annuler
          </button>
        )}
        <button type="submit" className={classe} disabled={texte.trim().length < 3}>
          {p.libelle}
        </button>
      </div>
    </form>
  );
}

function Qualification({ fiche, referentiel, envoyer, enCours }: { fiche: FicheIncidentDonnees; referentiel: ReferentielIncidents | undefined; envoyer: (p: { chemin: string; corps: unknown; succes: string }) => void; enCours: boolean }) {
  const [gravite, setGravite] = useState(String(fiche.gravite));
  const [type, setType] = useState(fiche.type);
  const [commentaire, setCommentaire] = useState('');
  return (
    <section className={fiche.gravite >= 4 ? 'a-faire rouge' : 'a-faire'} aria-labelledby="titre-qualification">
      <div>
        <span className="sur-titre">À faire maintenant, contrôle DGH</span>
        <h2 id="titre-qualification">Qualifier le signalement</h2>
        <p>Confirmez ou corrigez la gravité et le type ; le commentaire est conservé dans l’historique. La qualification ne porte aucune appréciation de responsabilité.</p>
      </div>
      <form
        className="action-motivee"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          envoyer({ chemin: 'qualification', corps: { gravite: Number(gravite), type, commentaire }, succes: 'Qualification enregistrée.' });
        }}
      >
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="q-gravite">Gravité</label>
            <select id="q-gravite" value={gravite} onChange={(e) => setGravite(e.target.value)}>
              {referentiel?.gravites.map((g) => (
                <option key={g.code} value={g.code}>
                  {g.libelle}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="q-type">Type</label>
            <select id="q-type" value={type} onChange={(e) => setType(e.target.value)}>
              {referentiel?.types.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="champ">
          <label htmlFor="q-commentaire">Commentaire</label>
          <textarea id="q-commentaire" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </div>
        <div className="actions">
          <button type="submit" className="bouton primaire" disabled={commentaire.trim().length < 3 || enCours}>
            Qualifier
          </button>
        </div>
      </form>
    </section>
  );
}

function JoindrePiece({ incidentId, surAjout }: { incidentId: string; surAjout: () => Promise<void> }) {
  const [fichier, setFichier] = useState<File | null>(null);
  const envoi = useMutation({
    mutationFn: async () => {
      const d = new FormData();
      d.append('fichier', fichier!);
      return api(`/api/v1/incidents/${incidentId}/pieces`, { formulaire: d });
    },
    onSuccess: async () => {
      setFichier(null);
      await surAjout();
    },
  });
  return (
    <div className="encart">
      <div className="champ">
        <label htmlFor="ji-fichier">Joindre une pièce (PDF, JPEG ou PNG, 10 Mo au plus)</label>
        <input id="ji-fichier" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Pièce refusée" />}
      <button type="button" className="bouton" disabled={!fichier || envoi.isPending} onClick={() => envoi.mutate()}>
        Joindre la pièce
      </button>
    </div>
  );
}
